import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@/lib/theme';
import api from '@/lib/api';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Toaster } from '@/components/ui/sonner';
import Home from '@/pages/Home';
import Login from '@/pages/Login';
import ReviewerLogin from '@/pages/ReviewerLogin';
import AuthCallback from '@/pages/AuthCallback';
import SellerProfile from '@/pages/SellerProfile';
import SellerPublicLiveProducts from '@/pages/SellerPublicLiveProducts';
import SellerPublicProducts from '@/pages/SellerPublicProducts';
import Schedule from '@/pages/Schedule';
import Dashboard from '@/pages/Dashboard';
import SellerDashboard from '@/pages/SellerDashboard';
import SellerDashboardV2 from '@/pages/SellerDashboardV2';
import SellerLiveControl from '@/pages/SellerLiveControl';
import SellerLiveConsole from '@/pages/SellerLiveConsole';
import SellerChannels from '@/pages/SellerChannels';
import LiveBroadcastView from '@/pages/LiveBroadcastView';
import LiveKitViewer from '@/pages/LiveKitViewer';
// STEP OVERLAY-2A · LiveKit Custom Web Template (RoomComposite custom_base_url)
import EgressTemplate from '@/pages/EgressTemplate';
import LiveOrderConfirm from '@/pages/LiveOrderConfirm';
import SellerApply from '@/pages/SellerApply';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminSellerApplications from '@/pages/AdminSellerApplications';
import AdminUsers from '@/pages/AdminUsers';
import AdminSellers from '@/pages/AdminSellers';
import AdminBroadcasts from '@/pages/AdminBroadcasts';
import AdminProducts from '@/pages/AdminProducts';
import AdminAds from '@/pages/AdminAds';
import SearchResults from '@/pages/SearchResults';
import BroadcastWaiting from '@/pages/BroadcastWaiting';
import BroadcastEnded from '@/pages/BroadcastEnded';
import ProductDetail from '@/pages/ProductDetail';
import ReplayPage from '@/pages/ReplayPage';
import ConsumerMini from '@/pages/ConsumerMini';
import Cart from '@/pages/Cart';
import Checkout from '@/pages/Checkout';
import Phase9Checkout from '@/pages/Phase9Checkout';
import { MyOrdersList, MyOrderDetail } from '@/pages/MyOrders';
import OrderPay from '@/pages/OrderPay';
import MyInquiries from '@/pages/MyInquiries';
import MyRma from '@/pages/MyRma';
import MyAddresses from '@/pages/MyAddresses';
import Profile from '@/pages/Profile';
import SellerNotificationSettings from '@/pages/SellerNotificationSettings';
import SellerNotificationLogs from '@/pages/SellerNotificationLogs';
import SellerCarrierAccounts from '@/pages/SellerCarrierAccounts';
import OperatorCarrierAccounts from '@/pages/OperatorCarrierAccounts';
import ConsumerClaimRequest from '@/pages/ConsumerClaimRequest';
import SellerClaims from '@/pages/SellerClaims';
import OperatorClaims from '@/pages/OperatorClaims';
import OperatorDashboard from '@/pages/OperatorDashboard';
import OperatorOrderDetail from '@/pages/OperatorOrderDetail';
import AdminOrders from '@/pages/AdminOrders';
import AdminCouponsGroupBuys from '@/pages/AdminCouponsGroupBuys';
import AdminShortClips from '@/pages/AdminShortClips';
import AdminHome from '@/pages/AdminHome';
import AdminMainPage from '@/pages/AdminMainPage';
import MainRenewal from '@/pages/MainRenewal';
import FeatureDetail from '@/pages/FeatureDetail';
import WhySelfLive from '@/pages/WhySelfLive';

// LIVEMOA-MAIN-PREVIEW-UNIFY-1 · Emergent Preview 호스트에서만 "/" = 리뉴얼 메인 (Production "/" 는 기존 쇼핑 홈)
const IS_PREVIEW_HOST = typeof window !== 'undefined' && window.location.hostname.endsWith('.preview.emergentagent.com');
import AdminKakaoBizmTest from '@/pages/AdminKakaoBizmTest';
import AdminLogisticsFoundation from '@/pages/AdminLogisticsFoundation';
import AdminPaymentOnboarding from '@/pages/AdminPaymentOnboarding';
import AdminInquiries from '@/pages/AdminInquiries';
import SellerProfileHomeSettings from '@/pages/SellerProfileHomeSettings';
import SellerGroupBuyCouponPage from '@/pages/SellerGroupBuyCouponPage';
import SellerShortClips from '@/pages/SellerShortClips';
// STEP SOCIAL-COMMENT-UI-1 · 통합 댓글관리 (Instagram + TikTok)
import SellerComments from '@/pages/SellerComments';
// STEP TIKTOK-ACCOUNTS-REVIEW-DEMO-1 · 심사 영상용 Prototype
import SellerTikTokReviewDemo from '@/pages/SellerTikTokReviewDemo';
import MyCoupons from '@/pages/MyCoupons';
import ShortClipFeed from '@/pages/ShortClipFeed';
import LiveOnly from '@/pages/LiveOnly';
import LiveWatchHub from '@/pages/LiveWatchHub';
// STEP GROWTH-B2 · FAN BOOST public landing (Kakao login → auto-follow)
import FanBoostLanding from '@/pages/FanBoostLanding';
// STEP GROWTH-B3 · LIVE BOOST public landing (Kakao login → auto-follow + LIVE reminder)
import LiveBoostLanding from '@/pages/LiveBoostLanding';
import ScrollToTop from '@/components/ScrollToTop';
import Terms from '@/pages/Terms';
import DataDeletion from '@/pages/DataDeletion';
import Privacy from '@/pages/Privacy';
// STEP MOA-LIVE-VOICE-1C-RCA · Preview-only Vosk 단독 테스트 Lab
import VoskLab from '@/pages/VoskLab';
import '@/App.css';

const ProtectedRoute = ({ user, children }) => {
  const location = useLocation();
  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?return_url=${returnUrl}`} replace />;
  }
  return children;
};

const isAdminAccount = (u) =>
  !!u && (u.is_admin === true || u.role === 'admin' || u.role === 'super_admin' || u.admin_role === 'admin' || u.admin_role === 'super_admin');

const AdminRoute = ({ user, children }) => {
  const location = useLocation();
  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?return_url=${returnUrl}`} replace />;
  }
  if (!isAdminAccount(user)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#0A0A0A] px-6" data-testid="admin-route-denied">
        <div className="text-center max-w-md">
          <div className="mx-auto mb-4 inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#FF3B30]/10 text-[#FF3B30] font-black text-xl">403</div>
          <h2 className="text-2xl font-black mb-2">관리자 권한이 없습니다.</h2>
          <p className="text-sm text-gray-500 mb-6">이 페이지는 관리자 전용입니다. 접근 권한이 필요하신 경우 관리자에게 문의해주세요.</p>
          <a href="/" className="inline-block px-4 py-2 rounded-full bg-black/5 dark:bg-white/10 text-sm font-bold hover:bg-black/10 dark:hover:bg-white/20">일반 사이트로 이동</a>
        </div>
      </div>
    );
  }
  return children;
};

const SellerOnlyRoute = ({ user, children }) => {
  const location = useLocation();
  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?return_url=${returnUrl}`} replace />;
  }
  if (user.seller_status !== 'approved') {
    return <Navigate to="/seller/apply" replace />;
  }
  return children;
};

function AppRouter() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (location.pathname === '/login' || location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    const checkAuth = async () => {
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [location.pathname, location.hash]);

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback setUser={setUser} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B30]"></div>
      </div>
    );
  }

  const isAdminRoute = location.pathname.startsWith('/admin');
  const hideChrome =
    location.pathname === '/login' ||
    location.pathname === '/auth-callback' ||
    location.pathname.startsWith('/live-view/') ||    // STEP 8-BUYER-LIVE-UI · video-first
    location.pathname.startsWith('/seller/live-console') ||   // STEP 8-LIVE-MOBILE-FULLSCREEN · Seller Console own layout
    location.pathname.startsWith('/egress-template/') ||      // STEP OVERLAY-2A · headless Chromium template (no chrome)
    location.pathname.replace(/\/+$/, '') === '/main-renewal' || // LIVEMOA-MAIN-RENEWAL-1 · own header
    (IS_PREVIEW_HOST && location.pathname === '/') ||         // LIVEMOA-MAIN-PREVIEW-UNIFY-1
    location.pathname.startsWith('/features/') ||             // LIVEMOA-FEATURE-DETAIL-STEP1 · own header/footer
    location.pathname.replace(/\/+$/, '') === '/why-self-live' || // LIVEMOA-SELF-LIVE-VALUE-1
    isAdminRoute;

  return (
    <div className="App">
      <ScrollToTop />
      {!hideChrome && <Navigation user={user} setUser={setUser} compact={location.pathname.startsWith('/seller/live-control')} />}
      <Routes>
        <Route path="/" element={IS_PREVIEW_HOST ? <MainRenewal user={user} /> : <Home />} />
        <Route path="/shopping-home" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/main-renewal" element={<MainRenewal user={user} />} />
        <Route path="/features/live-console" element={<FeatureDetail user={user} slug="live-console" featureKey="01" />} />
        <Route path="/why-self-live" element={<WhySelfLive user={user} />} />
        <Route path="/reviewer-login" element={<ReviewerLogin setUser={setUser} />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/privacy/data-deletion" element={<DataDeletion />} />
        <Route path="/auth-callback" element={<AuthCallback setUser={setUser} />} />
        <Route path="/seller/apply" element={<ProtectedRoute user={user}><SellerApply user={user} setUser={setUser} /></ProtectedRoute>} />
        <Route path="/seller/dashboard" element={<SellerOnlyRoute user={user}><SellerDashboardV2 user={user} /></SellerOnlyRoute>} />
        <Route path="/seller/live-control" element={<SellerOnlyRoute user={user}><SellerLiveControl user={user} /></SellerOnlyRoute>} />
        <Route path="/seller/live-console" element={<SellerOnlyRoute user={user}><SellerLiveConsole user={user} /></SellerOnlyRoute>} />
        {/* STEP LIVE-STUDIO-CONSOLE-SPLIT-1 · 채널 연결 전용 route.  BroadcastSelector
            의 [플랫폼 연결상태 확인] 이 PC Studio 가 아닌 이 route 로 이동한다. */}
        <Route path="/seller/channels" element={<SellerOnlyRoute user={user}><SellerChannels /></SellerOnlyRoute>} />
        <Route path="/seller/groupbuy-coupon" element={<SellerOnlyRoute user={user}><SellerGroupBuyCouponPage user={user} /></SellerOnlyRoute>} />
        <Route path="/seller/short-clips" element={<SellerOnlyRoute user={user}><SellerShortClips user={user} /></SellerOnlyRoute>} />
        {/* STEP SOCIAL-COMMENT-UI-1 · Seller 통합 댓글관리 */}
        <Route path="/seller/comments" element={<SellerOnlyRoute user={user}><SellerComments user={user} /></SellerOnlyRoute>} />
        {/* STEP TIKTOK-ACCOUNTS-REVIEW-DEMO-1 · 심사 영상용 Prototype (fixture only) */}
        <Route path="/seller/tiktok-review-demo" element={<SellerOnlyRoute user={user}><SellerTikTokReviewDemo user={user} /></SellerOnlyRoute>} />
        <Route path="/seller/profile-home" element={<SellerOnlyRoute user={user}><SellerProfileHomeSettings user={user} /></SellerOnlyRoute>} />
        <Route path="/short-clips" element={<ShortClipFeed />} />
        {/* STEP GROWTH-B2 · FAN BOOST public landing */}
        <Route path="/g/:sellerId" element={<FanBoostLanding user={user} />} />
        {/* STEP GROWTH-B3 · LIVE BOOST public landing */}
        <Route path="/gl/:campaignId" element={<LiveBoostLanding user={user} />} />
        <Route path="/my/coupons" element={<ProtectedRoute user={user}><MyCoupons /></ProtectedRoute>} />
        <Route path="/admin/coupons" element={<AdminRoute user={user}><AdminCouponsGroupBuys user={user} /></AdminRoute>} />
        <Route path="/admin/short-clips" element={<AdminRoute user={user}><AdminShortClips user={user} /></AdminRoute>} />
        <Route path="/admin/home" element={<AdminRoute user={user}><AdminHome user={user} /></AdminRoute>} />
        <Route path="/admin/main-page" element={<AdminRoute user={user}><AdminMainPage user={user} /></AdminRoute>} />
        <Route path="/seller/notifications" element={<SellerOnlyRoute user={user}><SellerNotificationSettings user={user} /></SellerOnlyRoute>} />
        <Route path="/seller/notifications/logs" element={<SellerOnlyRoute user={user}><SellerNotificationLogs user={user} /></SellerOnlyRoute>} />
        <Route path="/seller/carrier-accounts" element={<SellerOnlyRoute user={user}><SellerCarrierAccounts user={user} /></SellerOnlyRoute>} />
        <Route path="/operator/carrier-accounts" element={<ProtectedRoute user={user}><OperatorCarrierAccounts user={user} /></ProtectedRoute>} />
        <Route path="/orders/:orderId/claim/:type" element={<ProtectedRoute user={user}><ConsumerClaimRequest user={user} /></ProtectedRoute>} />
        <Route path="/orders/:orderId/claim" element={<ProtectedRoute user={user}><ConsumerClaimRequest user={user} /></ProtectedRoute>} />
        <Route path="/seller/claims" element={<SellerOnlyRoute user={user}><SellerClaims user={user} /></SellerOnlyRoute>} />
        <Route path="/operator/claims" element={<ProtectedRoute user={user}><OperatorClaims user={user} /></ProtectedRoute>} />
        <Route path="/operator" element={<ProtectedRoute user={user}><OperatorDashboard user={user} /></ProtectedRoute>} />
        <Route path="/operator/dashboard" element={<ProtectedRoute user={user}><OperatorDashboard user={user} /></ProtectedRoute>} />
        <Route path="/operator/orders/:sid" element={<ProtectedRoute user={user}><OperatorOrderDetail user={user} /></ProtectedRoute>} />
        <Route path="/live/all" element={<LiveOnly />} />
        <Route path="/live/hub/:externalId" element={<LiveWatchHub />} />
        <Route path="/live/:broadcastId" element={<LiveBroadcastView />} />
        <Route path="/live-view/:sessionId" element={<LiveKitViewer />} />
        {/* STEP OVERLAY-2A · LiveKit RoomComposite Custom Web Template
            headless Chromium (LiveKit Egress worker) 가 로드.
            Buyer / Seller UI 와 분리된 egress-전용 renderer. */}
        <Route path="/egress-template/:sessionId" element={<EgressTemplate />} />
        <Route path="/live-order/:candidateId" element={<LiveOrderConfirm user={user} />} />
        <Route path="/checkout/:cartId" element={<Phase9Checkout />} />
        <Route path="/seller-dashboard" element={<ProtectedRoute user={user}><SellerDashboard user={user} /></ProtectedRoute>} />
        <Route path="/seller/:sellerId" element={<SellerProfile user={user} />} />
        <Route path="/seller/:sellerId/live-products" element={<SellerPublicLiveProducts />} />
        <Route path="/seller/:sellerId/products" element={<SellerPublicProducts />} />
        <Route path="/schedule" element={<Schedule />} />
        <Route path="/broadcast/:broadcastId/waiting" element={<BroadcastWaiting />} />
        <Route path="/broadcast/:broadcastId/ended" element={<BroadcastEnded />} />
        <Route path="/product/:productId" element={<ProductDetail />} />
        <Route path="/replay/:sessionId" element={<ReplayPage />} />
        {/* STEP MINI-ORDER-FOUNDATION-1 · Consumer MINI (web).  LIVE Session 의
            current product 를 모바일에서 1-tap 주문. 신규 쇼핑몰이 아님 —
            기존 live-context polling + OptionSheet + inline-order REUSE. */}
        <Route path="/live/:sessionId/mini" element={<ConsumerMini />} />
        {/* STEP MINI-EXTERNAL-SESSION-IMPL-1 · 외부 SNS 전용 미니주문 · schedule 기반.
            streaming_sessions 미참조 · 외부 LIVE 방송 송출과 독립. */}
        <Route path="/live-mini/:scheduleId" element={<ConsumerMini />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<ProtectedRoute user={user}><Checkout /></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute user={user}><MyOrdersList /></ProtectedRoute>} />
        {/* KAKAO-UNPAID-ORDER-NOTICE-REPLACE-1 · BizM 승인 버튼 URL /mypage/orders → 기존 주문내역 */}
        <Route path="/mypage/orders" element={<Navigate to="/orders" replace />} />
        <Route path="/orders/:orderId/pay" element={<ProtectedRoute user={user}><OrderPay /></ProtectedRoute>} />
        <Route path="/orders/:orderId" element={<ProtectedRoute user={user}><MyOrderDetail /></ProtectedRoute>} />
        <Route path="/my/inquiries" element={<ProtectedRoute user={user}><MyInquiries /></ProtectedRoute>} />
        <Route path="/my/rma" element={<ProtectedRoute user={user}><MyRma /></ProtectedRoute>} />
        <Route path="/addresses" element={<ProtectedRoute user={user}><MyAddresses /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute user={user}><Profile /></ProtectedRoute>} />
        <Route path="/admin/orders" element={<AdminRoute user={user}><AdminOrders user={user} /></AdminRoute>} />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/dashboard" element={<ProtectedRoute user={user}><Dashboard user={user} setUser={setUser} /></ProtectedRoute>} />
        <Route path="/admin" element={<AdminRoute user={user}><AdminDashboard user={user} /></AdminRoute>} />
        <Route path="/admin/kakao-bizm-test" element={<AdminRoute user={user}><AdminKakaoBizmTest user={user} /></AdminRoute>} />
        <Route path="/admin/logistics" element={<AdminRoute user={user}><AdminLogisticsFoundation user={user} /></AdminRoute>} />
        <Route path="/admin/payment-onboarding" element={<AdminRoute user={user}><AdminPaymentOnboarding user={user} /></AdminRoute>} />
        <Route path="/admin/seller-applications" element={<AdminRoute user={user}><AdminSellerApplications user={user} /></AdminRoute>} />
        <Route path="/admin/users" element={<AdminRoute user={user}><AdminUsers user={user} /></AdminRoute>} />
        <Route path="/admin/sellers" element={<AdminRoute user={user}><AdminSellers user={user} /></AdminRoute>} />
        <Route path="/admin/broadcasts" element={<AdminRoute user={user}><AdminBroadcasts user={user} /></AdminRoute>} />
        <Route path="/admin/products" element={<AdminRoute user={user}><AdminProducts user={user} /></AdminRoute>} />
        <Route path="/admin/ads" element={<AdminRoute user={user}><AdminAds user={user} /></AdminRoute>} />
        <Route path="/admin/inquiries" element={<AdminRoute user={user}><AdminInquiries user={user} /></AdminRoute>} />
        {/* STEP MOA-LIVE-VOICE-1C-RCA · Preview-only 디버그 Lab (Production hostname 에서는 자체 404). */}
        <Route path="/vosk-lab" element={<ProtectedRoute user={user}><VoskLab user={user} /></ProtectedRoute>} />
      </Routes>
      {!hideChrome && <Footer user={user} />}
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
